/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { useApolloClient } from '@apollo/client';

import { breakpoints } from '@coursera/cds-common';
import { Button, Checkbox, CheckboxGroup, InlineNotification } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import SettingsActionCreators from 'bundles/account-settings/actions/SettingsActionCreators';
import { Sections, UpdateStates } from 'bundles/account-settings/constants/SettingsConstants';
import type { CourseraNormalizedEmailPreference, UpdateStatus } from 'bundles/account-settings/types/EmailPreferences';
import { getStatusMessages } from 'bundles/user-account/utils/errorMessageUtils';

import _t from 'i18n!nls/account-settings';

type Props = {
  emailPreferences: CourseraNormalizedEmailPreference[];
  updateStatus: UpdateStatus;
  disabled: boolean;
};

const styles = {
  checkboxGroup: css`
    .cds-checkboxAndRadio-label {
      margin-bottom: 0;
    }
  `,
  buttonRow: css`
    margin-block-start: var(--cds-spacing-200);
  `,
  saveButton: css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  inlineNotification: css`
    margin-block-start: var(--cds-spacing-200);
  `,
};

const isPreferenceChecked = (preference: CourseraNormalizedEmailPreference) => {
  return Object.entries(preference.value).some(([key, val]) => key === preference.id && val === true);
};

const PreferenceVisibilityTracker = ({
  preference,
  children,
}: {
  preference: CourseraNormalizedEmailPreference;
  children: React.ReactNode;
}) => {
  const ref: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker('view_checkbox', {
    checkbox: {
      name: 'coursera_communication_email_preference',
      label: preference.name,
      checked: isPreferenceChecked(preference),
    },
  });
  return <div ref={ref}>{children}</div>;
};

const CourseraEmailPreferencesList = ({ emailPreferences, updateStatus, disabled }: Props) => {
  const client = useApolloClient();
  const track = useTracker();
  const [preferences, setPreferences] = useState<CourseraNormalizedEmailPreference[]>(emailPreferences);
  const [toggledPreferencesForTracking, setToggledPreferencesForTracking] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setPreferences(emailPreferences);
  }, [emailPreferences]);

  const selectedValues = disabled ? [] : preferences.filter(isPreferenceChecked).map((p) => p.id);

  const handleCheckboxToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const toggledId = event.target.value;
    const updatedPreferences = preferences.map((pref) => {
      if (pref.id !== toggledId) return pref;
      const wasChecked = isPreferenceChecked(pref);
      return { ...pref, value: { ...pref.value, [pref.id]: !wasChecked } };
    });
    setPreferences(updatedPreferences);
    setToggledPreferencesForTracking((prev) => ({ ...prev, [toggledId]: true }));
    SettingsActionCreators.updatePendingChangesStatus(Sections.COURSERA_EMAIL_PREFERENCES, true);
  };

  const handleSave = () => {
    if (preferences.length === 0) return;
    SettingsActionCreators.updateEmailPreferences(Sections.COURSERA_EMAIL_PREFERENCES, preferences, client);
    // Same as old settings page: fire tracking events after save, not on toggle
    preferences.forEach((preference) => {
      if (toggledPreferencesForTracking[preference.id]) {
        track('click_checkbox', {
          checkbox: {
            name: 'coursera_communication_email_preference',
            label: preference.name,
            checked: isPreferenceChecked(preference),
          },
        });
      }
    });
    setToggledPreferencesForTracking({});
  };

  const isUpdating = updateStatus.state === UpdateStates.UPDATING;

  return (
    <div>
      <CheckboxGroup
        onChange={handleCheckboxToggle}
        label={_t('Communication from Coursera')}
        value={selectedValues}
        css={styles.checkboxGroup}
        optional
        necessityIndicator="none"
      >
        {preferences.map((preference) => (
          <PreferenceVisibilityTracker key={preference.id} preference={preference}>
            <Checkbox disabled={disabled} label={preference.name} value={preference.id} />
          </PreferenceVisibilityTracker>
        ))}
      </CheckboxGroup>

      <div css={styles.buttonRow}>
        <Button
          variant="secondary"
          size="small"
          loading={isUpdating}
          disabled={disabled}
          onClick={handleSave}
          css={styles.saveButton}
        >
          {_t('Save Changes')}
        </Button>
      </div>

      <div role="status" aria-atomic="true">
        {updateStatus.state === UpdateStates.SUCCEEDED && (
          <InlineNotification css={styles.inlineNotification} severity="success">
            {getStatusMessages.UPDATE_SUCCEEDED}
          </InlineNotification>
        )}
      </div>
      <div role="alert" aria-atomic="true">
        {updateStatus.state === UpdateStates.FAILED && (
          <InlineNotification css={styles.inlineNotification} severity="error">
            {getStatusMessages.UPDATE_FAILED}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};

export default CourseraEmailPreferencesList;
